import {
  Analytics,
  setUserId as firebaseSetUserId,
  setUserProperties as firebaseSetUserProperties,
  getAnalytics,
  logEvent
} from "firebase/analytics";

import { ANALYTICS_CONFIG } from "./config";

// Firebase Analytics instance
let analytics: Analytics | null = null;

// Initialize Firebase Analytics
export const initializeAnalytics = () => {
  if (!ANALYTICS_CONFIG.enabled) {
    console.warn("Analytics disabled");
    return;
  }

  try {
    analytics = getAnalytics();
    console.log("Firebase Analytics initialized");
  } catch (error) {
    console.error("Failed to initialize Firebase Analytics:", error);
  }
};

// Track page views
export const trackPageView = (pagePath: string, pageTitle?: string) => {
  if (!analytics) return;

  logEvent(analytics, "page_view", {
    page_path: pagePath,
    page_title: pageTitle || document.title,
    page_location: window.location.href
  });
};

// Track custom events
export const trackEvent = (
  eventName: string,
  parameters?: {
    event_category?: string;
    event_label?: string;
    value?: number;
    [key: string]: any;
  }
) => {
  if (!analytics) return;

  logEvent(analytics, eventName, parameters);
};

// Track user login
export const trackLogin = (method: string) => {
  trackEvent("login", {
    method: method
  });
};

// Track user signup
export const trackSignUp = (method: string) => {
  trackEvent("sign_up", {
    method: method
  });
};

// Track booking events
export const trackBookingStart = (eventId: string, eventName: string) => {
  trackEvent("booking_start", {
    event_category: "booking",
    event_label: eventName,
    event_id: eventId
  });
};

export const trackBookingComplete = (eventId: string, eventName: string, value?: number) => {
  trackEvent("booking_complete", {
    event_category: "booking",
    event_label: eventName,
    event_id: eventId,
    value: value
  });
};

export const trackBookingCancel = (eventId: string, eventName: string) => {
  trackEvent("booking_cancel", {
    event_category: "booking",
    event_label: eventName,
    event_id: eventId
  });
};

// Track quiz events
export const trackQuizStart = (quizId: string, quizName: string) => {
  trackEvent("quiz_start", {
    event_category: "quiz",
    event_label: quizName,
    quiz_id: quizId
  });
};

export const trackQuizComplete = (quizId: string, quizName: string, score: number) => {
  trackEvent("quiz_complete", {
    event_category: "quiz",
    event_label: quizName,
    quiz_id: quizId,
    score: score
  });
};

// Track credit/payment events
export const trackPaymentStart = (amount: number, currency: string = "VND") => {
  trackEvent("payment_start", {
    event_category: "payment",
    currency: currency,
    value: amount
  });
};

export const trackPaymentComplete = (amount: number, currency: string = "VND", method: string) => {
  trackEvent("payment_complete", {
    event_category: "payment",
    currency: currency,
    value: amount,
    payment_method: method
  });
};

// Track search events
export const trackSearch = (searchTerm: string, resultsCount?: number) => {
  trackEvent("search", {
    event_category: "engagement",
    search_term: searchTerm,
    results_count: resultsCount
  });
};

// Track user engagement
export const trackEngagement = (action: string, category: string, label?: string) => {
  trackEvent("engagement", {
    event_category: category,
    event_label: label,
    action: action
  });
};

// Track errors
export const trackError = (errorMessage: string, errorCode?: string) => {
  trackEvent("error", {
    event_category: "error",
    event_label: errorMessage,
    error_code: errorCode
  });
};

// Set user properties
export const setUserProperties = (properties: {
  user_id?: string;
  user_type?: string;
  [key: string]: any;
}) => {
  if (!analytics) return;

  firebaseSetUserProperties(analytics, properties);
};

// Set user ID
export const setUserId = (userId: string) => {
  if (!analytics) return;

  firebaseSetUserId(analytics, userId);
};

// Track conversion events
export const trackConversion = (conversionName: string, value?: number, currency?: string) => {
  trackEvent("conversion", {
    event_category: "conversion",
    event_label: conversionName,
    value: value,
    currency: currency
  });
};
